import React, {ReactElement, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import { createPortal, flushSync } from 'react-dom'
import styles from './index.module.scss'
import { getDropdownVerticalPosition } from './position'

const DROPDOWN_OPEN_EVENT_NAME = 'dropdown:open'

interface DropdownOpenEventDetail {
    dropdownId?: string
    ancestorDropdownIds?: string[]
}

interface DropDownProps{
    children: ReactElement,
    btn: ReactElement | ((isOpen: boolean) => ReactElement),
    closeDropDownByPick?: boolean,
    isOpen?: boolean,
    setIsOpen?: (isOpen: boolean) => void,
    position?: 'left' | 'right' | 'side-right',
    usePortal?: boolean,
    dropdownClassName?: string,
    contentClassName?: string,
    closeOtherDropdownsOnOpen?: boolean,
    verticalPlacement?: 'auto' | 'bottom' | 'top',
}

export default function DropDown({ children, btn, closeDropDownByPick = false, isOpen = false, setIsOpen = () => {}, position = 'left', usePortal = true, dropdownClassName = '', contentClassName = '', closeOtherDropdownsOnOpen = true, verticalPlacement = 'auto' }: DropDownProps){
    const [openDropDown, setOpenDropDown] = useState(isOpen)
    const dropdownRef = useRef<HTMLDivElement | null>(null)
    const dropdownMenuRef = useRef<HTMLDivElement | null>(null)
    const dropdownIdRef = useRef(`dropdown-${Math.random().toString(36).slice(2)}`)
    const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({})
    const dropdownRootSelector = `[data-dropdown-root="${dropdownIdRef.current}"]`

    const updatePortalPosition = useMemo(() => {
        return () => {
            if (!dropdownRef.current) {
                return
            }

            const rect = dropdownRef.current.getBoundingClientRect()
            const menuRect = dropdownMenuRef.current?.getBoundingClientRect()
            const dropdownHeight = menuRect?.height || 0

            if (position === 'side-right') {
                // Measure the trigger button itself (the parent wrapper can be
                // taller than the button when it's centered in the sidebar).
                const triggerEl = dropdownRef.current.firstElementChild as HTMLElement | null
                const triggerRect = (triggerEl ?? dropdownRef.current).getBoundingClientRect()
                const { top } = getDropdownVerticalPosition({
                    triggerTop: triggerRect.top,
                    triggerBottom: triggerRect.bottom,
                    dropdownHeight,
                    viewportHeight: window.innerHeight,
                })
                // Keep the menu pinned 12px from the viewport's left edge
                // (collapsed sidebar workspace menu).
                setPortalStyle({
                    top,
                    left: 12,
                })

                return
            }

            const top = verticalPlacement === 'bottom'
                ? rect.bottom + 10
                : verticalPlacement === 'top'
                    ? rect.top - 10 - dropdownHeight
                    : getDropdownVerticalPosition({
                        triggerTop: rect.top,
                        triggerBottom: rect.bottom,
                        dropdownHeight,
                        viewportHeight: window.innerHeight,
                    }).top

            if (position === 'right') {
                const dropdownWidth = menuRect?.width || 0
                const viewportGap = 16

                // Not enough room to the left of the trigger (e.g. it wrapped
                // onto a new line at the left edge) — open to the right instead.
                if (dropdownWidth && rect.right - dropdownWidth < viewportGap) {
                    setPortalStyle({
                        top,
                        left: Math.max(viewportGap, Math.min(rect.left, window.innerWidth - viewportGap - dropdownWidth)),
                    })

                    return
                }

                setPortalStyle({
                    top,
                    right: Math.max(viewportGap, window.innerWidth - rect.right),
                })

                return
            }

            setPortalStyle({
                top,
                left: rect.left,
                width: rect.width,
                // Exposed so consumers can opt into a flexible width (e.g. grow up
                // to 1.5x the trigger) via a class while the default stays fixed.
                ['--dropdown-trigger-width' as string]: `${rect.width}px`,
            })
        }
    }, [position, verticalPlacement])

    const closeDropDownByPickHandler = () => {
        if(closeDropDownByPick){
            flushSync(() => {
                setOpenDropDown(false)
                setIsOpen(false)
            })
        }
    }


    const openDropDownHandler = () => {
        const nextOpenState = !openDropDown

        setOpenDropDown(nextOpenState)
        setIsOpen(nextOpenState)

        if (nextOpenState && closeOtherDropdownsOnOpen) {
            const ancestorDropdownIds = new Set<string>()
            let ancestor = dropdownRef.current?.parentElement?.closest<HTMLElement>('[data-dropdown-root]') || null

            while (ancestor) {
                const ancestorId = ancestor.dataset.dropdownRoot
                if (ancestorId) ancestorDropdownIds.add(ancestorId)
                ancestor = ancestor.parentElement?.closest<HTMLElement>('[data-dropdown-root]') || null
            }

            document.dispatchEvent(new CustomEvent(DROPDOWN_OPEN_EVENT_NAME, {
                detail: {
                    dropdownId: dropdownIdRef.current,
                    ancestorDropdownIds: Array.from(ancestorDropdownIds),
                },
            }))
        }
    }

    useEffect(() => {
        setOpenDropDown(isOpen)
    }, [isOpen])

    useLayoutEffect(() => {
        if (!openDropDown) {
            return
        }

        updatePortalPosition()

        const updateOnFrame = () => updatePortalPosition()

        window.addEventListener('resize', updateOnFrame)
        window.addEventListener('scroll', updateOnFrame, true)
        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(updateOnFrame)
            : null

        if (dropdownMenuRef.current) {
            resizeObserver?.observe(dropdownMenuRef.current)
        }

        return () => {
            window.removeEventListener('resize', updateOnFrame)
            window.removeEventListener('scroll', updateOnFrame, true)
            resizeObserver?.disconnect()
        }
    }, [openDropDown, updatePortalPosition])

    useEffect(() => {
        const clickOnOverlay = (e: Event) => {
            if (e.target instanceof HTMLElement) {
                if (!e.target.closest(dropdownRootSelector)) {
                    setOpenDropDown(false)
                    setIsOpen(false)
                }
            }
        }

        if(openDropDown){
            document.addEventListener('mousedown', clickOnOverlay, true)
        }

        return () => document.removeEventListener("mousedown", clickOnOverlay, true)
    }, [dropdownRootSelector, openDropDown, setIsOpen])

    useEffect(() => {
        const handleDropdownOpen = (event: Event) => {
            const customEvent = event as CustomEvent<DropdownOpenEventDetail>
            const isAncestorOfOpenedDropdown = customEvent.detail?.ancestorDropdownIds?.includes(dropdownIdRef.current)

            if (customEvent.detail?.dropdownId !== dropdownIdRef.current && !isAncestorOfOpenedDropdown) {
                setOpenDropDown(false)
                setIsOpen(false)
            }
        }

        document.addEventListener(DROPDOWN_OPEN_EVENT_NAME, handleDropdownOpen)

        return () => document.removeEventListener(DROPDOWN_OPEN_EVENT_NAME, handleDropdownOpen)
    }, [setIsOpen])

    const dropdownNode = openDropDown ? (
        <div
            ref={dropdownMenuRef}
            data-dropdown-root={dropdownIdRef.current}
            className={`${styles.dropdown} ${position === 'right' ? styles.dropdown_right : ''} ${(position === 'side-right' && !usePortal) ? styles.dropdown_sideRight : ''} ${usePortal ? styles.dropdown_portal : ''} ${dropdownClassName}`}
            onClick={closeDropDownByPickHandler}
            style={usePortal ? portalStyle : undefined}
        >
            <div className={`${styles.dropdown__content} ${contentClassName}`}>
                {children}
            </div>
        </div>
    ) : null

    return(
        <div className={styles.dropdown__parent} data-dropdown-root={dropdownIdRef.current} ref={dropdownRef}>
            <div onClick={openDropDownHandler}>
                {typeof btn === 'function' ? btn(openDropDown) : btn}
            </div>
            {!usePortal && dropdownNode}
            {usePortal && openDropDown && typeof document !== 'undefined' && createPortal(dropdownNode, document.body)}
        </div>
    )
}
